// @flow
import React, { Component, Fragment } from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames/bind';
import { FormattedMessage, intlShape } from 'react-intl';
import { animateScroll as scroll } from 'react-scroll';
import Banner from '../../../elements/Banner/Banner';
import { Link, Redirect, Route } from 'react-router-dom';
import { Switch } from 'react-router';
import PurchaseContainer from '../Purchase/containers/PurchaseContainer';
import SaleContainer from '../Sale/containers/SaleContainer';
import Messaging from './Messaging/Messaging';
import AlertContainer from '../../alert/container/AlertContainer';
import StateTracker, { StateTrackerLoading } from './StateTracker/StateTracker';
import { purchaseInnerTrackingStates, saleInnerTrackingState, stateConverter } from '../utils/stateConverter';
import { LoadingContentStripe } from '../../../elements/LoadingContentFigures/LoadingContentFigures';
import { getLocalizedCountry } from '../../local-values/utils/countries';
import NotFound from '../../error/components/404/404';
import analytics from '../../../analytics';
import routes from '../../../routes';
import Page from '../../../Page';

import style from './Order.scss';
import icons from '../../../assets/styles/icons.scss';
import loadingStripeStyle from '../../../elements/LoadingContentFigures/LoadingContentFigures.scss';

import type { Order, OrderMessage, User } from '../../../entities';
import type { HTTPError } from '../../../error';
import type { Location } from '../../../router';
import type { OrderType } from '../duck';
import type { StateConvertResponse } from '../utils/stateConverter';
import type { LocalValuesState } from '../../local-values/duck';
import type { LocaleState } from '../../locale/duck';

type Props = {
  +location: Location;
  +localValues: LocalValuesState;
  +user?: User;
  +order?: Order;
  +isFetchingOrder?: boolean;
  +fetchMessages: (orderId: string) => void;
  +isFetchingMessages?: boolean;
  +messages?: any[];
  +fetchMessagesError?: HTTPError;
  +fetchOrderError?: HTTPError;
  +resetState: () => void;
  +resetAlertState: () => void;
  +addMessage: (message: OrderMessage) => void;
  +queueMessage: (queuedMessage: OrderMessage) => void;
  +unQueueMessage: (queuedMessageKey: number) => void;
  +postMessage: (orderId: string, message: OrderMessage) => void;
  +fetchOrder: (orderId: string, orderType: OrderType) => void;
  +resetMessages: () => void;
  +locale: LocaleState;
};

// This orderId is for showing in the banner only,
// For the api requests, each child must retrieve from route match
type State = {
  orderId: string;
  orderType: OrderType;
};

// collision with Order type
export default class OrderComponent extends Component<Props, State> {
  state = {
    orderId: '',
    orderType: 'purchase',
  };

  constructor(props: Props) {
    super(props);
    const split = props.location.pathname.split('/');
    this.state = {
      orderType: (split[1]: any),
      orderId: props.order ? props.order.id : (split[3]: any),
    };
  }

  componentDidUpdate(prevProps: Props) {
    const { order, messages, fetchMessages, location, resetMessages, locale, fetchOrder } = this.props;
    // When order is just deleted, then recalculate all the properties
    if (!!prevProps.order && !order) {
      const split = location.pathname.split('/');
      this.setState({
        orderType: (split[1]: any),
        orderId: (split[3]: any),
      }, () => {
        const { orderId, orderType } = this.state;
        fetchOrder(orderId, orderType);
      });
      resetMessages();
    }
    // When just fetched order
    if (!prevProps.order && !!order) {
      this.setState({
        orderId: order.id,
      });
      analytics.fireCustomEvent('Order Viewed', {
        category: 'Order',
        action: 'Viewed',
        label: order.id,
        user_type: this.state.orderType === 'sale' ? 'traveler' : 'purchaser',
        user_referrer: window.localStorage.getItem('userReferrer') || '',
        landing_url: window.localStorage.getItem('landingUrl') || '',
        domain_country: locale.domainCountry,
        order_id: order.id,
        order_formatted_id: order.id,
      });
      if (this.shouldHaveMessages(order.state)) {
        fetchMessages(order.id);
      }
    }
    // When order changed state
    if (!!prevProps.order && !!order && prevProps.order.state !== order.state) {
      this.scrollAnimateTop();
    }
  }

  componentWillUnmount() {
    const { resetAlertState, resetState } = this.props;
    resetAlertState();
    resetState();
  }

  componentDidMount() {
    const { fetchMessages, messages, order, fetchOrder } = this.props;
    const { orderId, orderType } = this.state;

    if (!order && orderId) {
      fetchOrder(orderId, orderType);
    }

    if (!messages && !!order && this.shouldHaveMessages(order.state)) {
      fetchMessages(order.id);
    }
  }

  shouldHaveMessages = (state: string) =>
    ['CREATED', 'CREATED_FOR_COURIER', 'AVAILABLE', 'AVAILABLE_FOR_COURIER', 'RESERVED', 'IN_COURIER_FLOW',
      'BUYER_CANCELED', 'QEMPO_CANCELED', 'TRAVELER_CANCELED', 'RETURNED', 'QEMPO_CANCELED_FOR_FRAUD', 'ABANDONED',
      'EXPIRED', 'DELIVERY_CONFIRMED'].indexOf(state) === -1;

  scrollAnimateTop = () =>
    scroll.scrollTo(0, {
      duration: 400,
    });

  hasNotFoundError(fetchOrderError?: HTTPError): boolean {
    if (!fetchOrderError) {
      return false;
    }
    return ((fetchOrderError.code === 401 && fetchOrderError.data &&
      fetchOrderError.data.name === 'QempoUnauthorizedError')
      || fetchOrderError.code === 404);

  }

  render() {
    const {
      user, order, messages, postMessage, addMessage, queueMessage, unQueueMessage, localValues,
      fetchOrderError, isFetchingOrder,
    } = this.props;
    const { orderType, orderId } = this.state;
    /*const receiver = order && order.purchaser && order.traveler && user &&
      (user.id !== order.purchaser.id ? order.purchaser : order.traveler);*/
    const receiver = user && order && (!!order.purchaser && order.purchaser.id !== user.id ?
      order.purchaser : order.traveler);
    const convertedState: ?StateConvertResponse = order && stateConverter(orderType === 'sale', order.state);
    const title = <FormattedMessage id="banner.back">{
      txt => <h1>
        <Link to={orderType === 'sale' && !!order ? `/flight/${order.flight.id}/sales` : '/orders/active'}>
          <i className={icons.iconChevronLeftMedium}/>{txt}
        </Link>
      </h1>
    }</FormattedMessage>;
    const rightElement = <FormattedMessage id={'banner.order'}>{
      txt => <p>{txt} {!!order ? order.id : orderId}</p>
    }</FormattedMessage>;
    return (
      <Fragment>
        {this.hasNotFoundError(fetchOrderError) && !order && <NotFound/>}
        {(!!order || isFetchingOrder) &&
        <Banner color={orderType === 'purchase' ? 'blue' : 'orange'}
                titleElement={title}
                rightElement={rightElement}/>}
        <section className={classNames(style.orderMainWrap,
          { [style.hidden]: this.hasNotFoundError(fetchOrderError) && !order })}>
          <AlertContainer showId="order"/>
          {(!convertedState || (!!convertedState && convertedState.innerState !== '0')) &&
          <Fragment>
            {!convertedState || isFetchingOrder || !order ?
              <article className={style.loadingWrap}>
                <LoadingContentStripe colorClassName={loadingStripeStyle.black100}/>
              </article> :
              convertedState.labelIntl &&
              <FormattedMessage id={convertedState.labelIntl}>{
                txt =>
                  <h2
                    className={classNames(style.stepTitle, {
                      [style.lightBlue]: convertedState.innerState !== '0' && orderType === 'purchase',
                      [style.orange]: convertedState.innerState !== '0' && orderType === 'sale',
                    })}>
                    {txt} {(convertedState.innerState === '3p' || convertedState.innerState === '3s')
                  && ` ${getLocalizedCountry(order.deliveryCountry.code, this.context.intl.formatMessage, localValues.countriesMap)}${convertedState.innerState === '3p' ? '!' : ''}`}
                  </h2>
              }</FormattedMessage>}
            {!convertedState || isFetchingOrder || !order ?
              <article className={style.loadingWrap}>
                <LoadingContentStripe colorClassName={loadingStripeStyle.black100}/>
              </article> :
              convertedState.labelIntlLong &&
              <FormattedMessage id={convertedState.labelIntlLong}>{
                txt =>
                  <h3
                    className={classNames(style.stepSubtitle, {
                      [style.lightBlue]: convertedState.innerState !== '0' && orderType === 'purchase',
                      [style.orange]: convertedState.innerState !== '0' && orderType === 'sale',
                    })}>
                    {txt} {(order.state === 'IN_COURIER_FLOW' || order.state === 'RECEIVED') &&
                  ` ${getLocalizedCountry(order.deliveryCountry.code, this.context.intl.formatMessage, localValues.countriesMap)}`}
                  </h3>
              }</FormattedMessage>}
            <article className={style.stateTrackerWrap}>
              {!convertedState || isFetchingOrder || !order ?
                <StateTrackerLoading
                  isSale={orderType === 'sale'}
                  formatMessage={this.context.intl.formatMessage}
                  countriesMap={localValues.countriesMap}
                /> :
                <StateTracker
                  states={orderType === 'purchase' ? purchaseInnerTrackingStates : saleInnerTrackingState}
                  color={orderType === 'purchase' ? 'lightBlue' : 'orange'}
                  selectedState={convertedState.innerState}
                  hasExtraHalf={convertedState.hasExtra}
                  deliveryCountryCode={order.deliveryCountry.code}
                  formatMessage={this.context.intl.formatMessage}
                  countriesMap={localValues.countriesMap}
                />}
            </article>
          </Fragment>}
          <section className={style.routeContent}>
            <Switch>
              <Route path={routes.PURCHASE.routePath}
                     render={props => <Page name={routes.PURCHASE.name}
                                            identifier={orderId}>
                       <PurchaseContainer {...props}/>
                     </Page>}/>
              <Route path={routes.SALE.routePath}
                     render={props => <Page name={routes.SALE.name}
                                            identifier={orderId}>
                       <SaleContainer {...props}/>
                     </Page>}/>
            </Switch>
          </section>
        </section>
        {user && order && !!order.serviceTier && this.shouldHaveMessages(order.state) &&
        <Messaging key="messaging"
                   userId={user.id}
                   user={user}
                   orderType={orderType}
                   postMessage={postMessage}
                   color={orderType === 'purchase' ? 'orange' : 'blue'}
                   addMessage={addMessage}
                   queueMessage={queueMessage}
                   unQueueMessage={unQueueMessage}
                   orderMessages={messages || []}
                   orderId={order.id}
                   order={order}
                   receiver={receiver}/>}
      </Fragment>);
  }
}

OrderComponent.propTypes = {
  location: PropTypes.object.isRequired,
  localValues: PropTypes.object.isRequired,
  user: PropTypes.object,
  order: PropTypes.object,
  isFetchingOrder: PropTypes.bool,
  fetchMessages: PropTypes.func.isRequired,
  isFetchingMessages: PropTypes.bool,
  messages: PropTypes.array,
  fetchMessagesError: PropTypes.object,
  fetchOrderError: PropTypes.object,
  resetState: PropTypes.func.isRequired,
  resetAlertState: PropTypes.func.isRequired,
  addMessage: PropTypes.func.isRequired,
  queueMessage: PropTypes.func.isRequired,
  unQueueMessage: PropTypes.func.isRequired,
  postMessage: PropTypes.func.isRequired,
  fetchOrder: PropTypes.func.isRequired,
  resetMessages: PropTypes.func.isRequired,
  locale: PropTypes.object.isRequired,
};

OrderComponent.contextTypes = {
  intl: intlShape,
};



// WEBPACK FOOTER //
// ./src/modules/order/components/Order.jsx